import { forwardRef } from "react";
import Portrait from "./Portrait";
import type { ChatCopy } from "./copy";

interface ChatLauncherProps {
  copy: ChatCopy;
  open: boolean;
  onOpen: () => void;
  onPreload: () => void;
}

/**
 * The always-visible pill. Stays mounted while the panel is open (the panel covers it) so
 * focus has a real node to return to on close — the N&S widget lost focus to <body> when
 * its launcher unmounted.
 */
const ChatLauncher = forwardRef<HTMLButtonElement, ChatLauncherProps>(({ copy, open, onOpen, onPreload }, ref) => (
  <button
    ref={ref}
    type="button"
    onClick={onOpen}
    onMouseEnter={onPreload}
    onFocus={onPreload}
    onTouchStart={onPreload}
    aria-haspopup="dialog"
    aria-expanded={open}
    aria-label={copy.launcherAria}
    tabIndex={open ? -1 : undefined}
    data-chat-launcher="1"
    className="fixed z-[9990] inline-flex min-h-[56px] items-center gap-3 rounded-full bg-metro-orange-500 pl-2 pr-5 text-sm font-semibold text-dark-950 shadow-2xl shadow-black/40 transition-colors hover:bg-metro-orange-400 focus:outline-none focus-visible:ring-4 focus-visible:ring-metro-orange-300 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-950 motion-reduce:transition-none right-4 bottom-[max(1rem,env(safe-area-inset-bottom))] sm:right-6 sm:bottom-6"
  >
    <Portrait size={40} className="ring-2 ring-dark-950/20" />
    <span className="whitespace-nowrap leading-none">{copy.launcher}</span>
    <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.7 8.7 0 0 1-3.8-.9L3 21l1.9-4.6A8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5z" />
    </svg>
  </button>
));

ChatLauncher.displayName = "ChatLauncher";

export default ChatLauncher;
